import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, Edit, Delete, Search as SearchIcon } from '@mui/icons-material';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';

const TestManagement = () => {
  const [tests, setTests] = useState([]);
  const [labs, setLabs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    sampleType: '',
    phlebotomy: 'Not Required',
    location: '',
    shippingTimeMin: 2,
    shippingTimeMax: 5,
    processingTime: 3,
    lab: '',
    testInformationPage: '',
    patientCollectionInstructions: '',
    sampleReport: '',
    biomarkers: [],
  });
  const [biomarkersText, setBiomarkersText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const itemsPerPage = 10;

  // URL validation function
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Filter tests based on search query
  const filteredTests = tests.filter((test) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      test.name?.toLowerCase().includes(query) ||
      test.category?.name?.toLowerCase().includes(query) ||
      test.sampleType?.name?.toLowerCase().includes(query) ||
      test.lab?.name?.toLowerCase().includes(query) ||
      test.phlebotomy?.toLowerCase().includes(query)
    );
  });

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    fetchTests();
    fetchLabs();
    fetchCategories();
    fetchSampleTypes();
    fetchLocations();
  }, []);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/tests');
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch tests',
        confirmButtonColor: '#1976d2',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLabs = async () => {
    try {
      const response = await api.get('/labs');
      setLabs(response.data);
    } catch (error) {
      console.error('Error fetching labs:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSampleTypes = async () => {
    try {
      const response = await api.get('/sampleTypes');
      setSampleTypes(response.data);
    } catch (error) {
      console.error('Error fetching sample types:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleOpenDialog = (test = null) => {
    if (test) {
      setSelectedTest(test);
      setFormData({
        name: test.name,
        description: test.description || '',
        category: test.category?._id || '',
        sampleType: test.sampleType?._id || '',
        phlebotomy: test.phlebotomy || 'Not Required',
        location: test.location?._id || '',
        shippingTimeMin: test.shippingTimeMin !== undefined ? test.shippingTimeMin : 2,
        shippingTimeMax: test.shippingTimeMax !== undefined ? test.shippingTimeMax : 5,
        processingTime: test.processingTime !== undefined ? test.processingTime : 3,
        lab: test.lab?._id || '',
        testInformationPage: test.testInformationPage || '',
        patientCollectionInstructions: test.patientCollectionInstructions || '',
        sampleReport: test.sampleReport || '',
        biomarkers: test.biomarkers || [],
      });
      setBiomarkersText((test.biomarkers || []).join('\n'));
    } else {
      setSelectedTest(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      sampleType: '',
      phlebotomy: 'Not Required',
      location: '',
      shippingTimeMin: 2,
      shippingTimeMax: 5,
      processingTime: 3,
      lab: '',
      testInformationPage: '',
      patientCollectionInstructions: '',
      sampleReport: '',
      biomarkers: [],
    });
    setBiomarkersText('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTest(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      sampleType: '',
      phlebotomy: 'Not Required',
      location: '',
      shippingTimeMin: 2,
      shippingTimeMax: 5,
      processingTime: 3,
      lab: '',
      testInformationPage: '',
      patientCollectionInstructions: '',
      sampleReport: '',
      biomarkers: [],
    });
    setBiomarkersText('');
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    // Validate required fields
    if (!formData.name.trim()) {
      return;
    }
    if (!formData.lab) {
      return;
    }
    if (!formData.category) {
      return;
    }
    if (!formData.sampleType) {
      return;
    }
    if (formData.testInformationPage && !isValidUrl(formData.testInformationPage)) {
      return;
    }
    if (formData.patientCollectionInstructions && !isValidUrl(formData.patientCollectionInstructions)) {
      return;
    }
    if (formData.sampleReport && !isValidUrl(formData.sampleReport)) {
      return;
    }

    try {
      // Process biomarkers from text input
      const processedBiomarkers = biomarkersText
        .split('\n')
        .map(item => item.trim())
        .filter(item => item !== '');

      const submitData = {
        ...formData,
        biomarkers: processedBiomarkers,
        category: formData.category,
        sampleType: formData.sampleType,
        location: formData.location || null,
        lab: formData.lab || null,
      };
      if (selectedTest) {
        await api.put(`/admin/tests/${selectedTest._id}`, submitData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Test updated successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await api.post('/admin/tests', submitData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Test created successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      }
      handleCloseDialog();
      fetchTests();
    } catch (err) {
      // Don't show alert for validation errors (400 status) as field-level validation handles them
      if (err.response?.status !== 400) {
        SwalWithA11yFix({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Operation failed',
          confirmButtonColor: '#1976d2',
        });
      }
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/tests/${selectedTest._id}`);
      SwalWithA11yFix({
        icon: 'success',
        title: 'Success!',
        text: 'Test deleted successfully',
        timer: 2000,
        showConfirmButton: false,
      });
      setDeleteDialogOpen(false);
      setSelectedTest(null);
      fetchTests();
    } catch (err) {
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Delete failed',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  return (
    <Box>

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            placeholder="Search tests..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
            }}
            sx={{
              width: { xs: '100%', sm: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              },
            }}
          >
            Add Test
          </Button>
        </Box>
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sample Type</TableCell>
                <TableCell>Phlebotomy</TableCell>
                <TableCell>Lab</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTests.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((test) => (
              <TableRow key={test._id}>
                <TableCell>{test.name}</TableCell>
                <TableCell>{test.category?.name || '-'}</TableCell>
                <TableCell>{test.sampleType?.name || '-'}</TableCell>
                <TableCell>{test.phlebotomy || 'Not Required'}</TableCell>
                <TableCell>{test.lab?.name || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(test)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedTest(test);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!loading && tests.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredTests.length / itemsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          {selectedTest ? 'Edit Test' : 'Create Test'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              error={!formData.name.trim() && submitted}
              helperText={!formData.name.trim() && submitted ? 'Test name is required' : ''}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Lab</InputLabel>
                <Select
                  value={formData.lab}
                  onChange={(e) => setFormData({ ...formData, lab: e.target.value })}
                  label="Lab"
                  error={!formData.lab && submitted}
                >
                  <MenuItem value="">None</MenuItem>
                  {labs.map((lab) => (
                    <MenuItem key={lab._id} value={lab._id}>
                      {lab.name}
                    </MenuItem>
                  ))}
                </Select>
                {!formData.lab && submitted && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    Lab is required
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                  error={!formData.category && submitted}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {!formData.category && submitted && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    Category is required
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Sample Type</InputLabel>
                <Select
                  value={formData.sampleType}
                  onChange={(e) => setFormData({ ...formData, sampleType: e.target.value })}
                  label="Sample Type"
                  error={!formData.sampleType && submitted}
                >
                  {sampleTypes.map((sampleType) => (
                    <MenuItem key={sampleType._id} value={sampleType._id}>
                      {sampleType.name}
                    </MenuItem>
                  ))}
                </Select>
                {!formData.sampleType && submitted && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    Sample type is required
                  </Typography>
                )}
              </FormControl>
            </Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Phlebotomy</InputLabel>
              <Select
                value={formData.phlebotomy}
                onChange={(e) => setFormData({ ...formData, phlebotomy: e.target.value })}
                label="Phlebotomy"
              >
                <MenuItem value="Required">Required</MenuItem>
                <MenuItem value="Not Required">Not Required</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
              <TextField
                fullWidth
                label="Min Shipping Time (Days)"
                type="number"
                value={formData.shippingTimeMin}
                onChange={(e) => setFormData({ ...formData, shippingTimeMin: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                label="Max Shipping Time (Days)"
                type="number"
                value={formData.shippingTimeMax}
                onChange={(e) => setFormData({ ...formData, shippingTimeMax: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Box>
            <TextField
              fullWidth
              label="Avg. Sample Processing Time (Business Days)"
              type="number"
              value={formData.processingTime}
              onChange={(e) => setFormData({ ...formData, processingTime: parseInt(e.target.value) || 0 })}
              margin="normal"
              inputProps={{ min: 0 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, mb: 1 }}>
              <TextField
                fullWidth
                label="Test Information page"
                value={formData.testInformationPage}
                onChange={(e) => setFormData({ ...formData, testInformationPage: e.target.value })}
                placeholder="Enter URL (e.g., https://example.com)"
                type="url"
                error={submitted && formData.testInformationPage && !isValidUrl(formData.testInformationPage)}
                helperText={submitted && formData.testInformationPage && !isValidUrl(formData.testInformationPage) ? 'Please enter a valid URL' : ''}
              />
              <TextField
                fullWidth
                label="Patient Collection Instructions"
                value={formData.patientCollectionInstructions}
                onChange={(e) => setFormData({ ...formData, patientCollectionInstructions: e.target.value })}
                placeholder="Enter URL (e.g., https://example.com)"
                type="url"
                error={submitted && formData.patientCollectionInstructions && !isValidUrl(formData.patientCollectionInstructions)}
                helperText={submitted && formData.patientCollectionInstructions && !isValidUrl(formData.patientCollectionInstructions) ? 'Please enter a valid URL' : ''}
              />
              <TextField
                fullWidth
                label="Sample Report"
                value={formData.sampleReport}
                onChange={(e) => setFormData({ ...formData, sampleReport: e.target.value })}
                placeholder="Enter URL (e.g., https://example.com)"
                type="url"
                error={submitted && formData.sampleReport && !isValidUrl(formData.sampleReport)}
                helperText={submitted && formData.sampleReport && !isValidUrl(formData.sampleReport) ? 'Please enter a valid URL' : ''}
              />
            </Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Location</InputLabel>
              <Select
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                label="Location"
              >
                <MenuItem value="">None</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location._id} value={location._id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Biomarkers"
              value={biomarkersText}
              onChange={(e) => setBiomarkersText(e.target.value)}
              margin="normal"
              multiline={true}
              rows={4}
              placeholder="Enter each biomarker on a new line"
              helperText="Enter each biomarker on a separate line"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            {selectedTest ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>Delete Test</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedTest?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#c62828',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestManagement;
